// components/ForgotPassword.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Button, ToggleButtonGroup, ToggleButton, Snackbar, Alert } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { ColorRing } from "react-loader-spinner";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import bgImage from "assets/images/bg-sign-in2-basic.webp";
import logoImage from "assets/images/moona-logo.png";
import banyanImage from "assets/images/logos/Banyan.jpg";

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1); // 1: Request, 2: Verify, 3: Reset
  const [method, setMethod] = useState('email'); // 'email' or 'phone'
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();

  // Auto-hide messages after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && step === 2) {
      setCanResend(true);
    }
  }, [resendTimer, step]);

const API_BASE_URL = 'https://seltentechnologiesdemo.com/moona-backend/ForgotPasswordApi.php';

const handleRequestReset = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');
  setMessage('');

  try {
    const payload = method === 'email' ? { email } : { phone_number: phoneNumber };

    const response = await axios.post(
      `${API_BASE_URL}?action=request`,
      payload
    );

    if (response.data.success) {
      setMessage(response.data.message);
      setStep(2);
      setResendTimer(60);
      setCanResend(false);
    } else {
      setError(response.data.message || 'Failed to send verification code');
    }
  } catch (err) {
    setError(err.response?.data?.message || 'An error occurred');
  } finally {
    setLoading(false);
  }
};

const handleResendCode = async () => {
  setLoading(true);
  setError('');
  setMessage('');

  try {
    const payload = method === 'email' ? { email } : { phone_number: phoneNumber };

    const response = await axios.post(
      `${API_BASE_URL}?action=request`,
      payload
    );

    if (response.data.success) {
      setMessage('Verification code resent successfully');
      setResendTimer(60);
      setCanResend(false);
    } else {
      setError(response.data.message || 'Failed to resend verification code');
    }
  } catch (err) {
    setError(err.response?.data?.message || 'An error occurred');
  } finally {
    setLoading(false);
  }
};

const handleVerifyCode = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    const payload = {
      verification_code: verificationCode,
      ...(method === 'email' ? { email } : { phone_number: phoneNumber }),
    };

    const response = await axios.post(
      `${API_BASE_URL}?action=verify`,
      payload
    );

    if (response.data.success) {
      setMessage('Code verified successfully');
      setStep(3);
    } else {
      setError(response.data.message || 'Invalid verification code');
    }
  } catch (err) {
    setError(err.response?.data?.message || 'Verification failed');
  } finally {
    setLoading(false);
  }
};

const handleResetPassword = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  if (newPassword !== confirmPassword) {
    setError('Passwords do not match');
    setLoading(false);
    return;
  }

  if (newPassword.length < 6) {
    setError('Password must be at least 6 characters long');
    setLoading(false);
    return;
  }

  try {
    const payload = {
      new_password: newPassword,
      verification_code: verificationCode,
      ...(method === 'email' ? { email } : { phone_number: phoneNumber }),
    };

    const response = await axios.post(
      `${API_BASE_URL}?action=reset`,
      payload
    );

    if (response.data.success) {
      setMessage('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/authentication/sign-in');
      }, 1500);
    } else {
      setError(response.data.message || 'Password reset failed');
    }
  } catch (err) {
    setError(err.response?.data?.message || 'Reset failed');
  } finally {
    setLoading(false);
  }
};


  const resetFlow = () => {
    setStep(1);
    setEmail('');
    setPhoneNumber('');
    setVerificationCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setMessage('');
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox>
          <MDTypography
            variant="h4"
            fontWeight="medium"
            color="white"
            mt={1}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
              <img src={logoImage} alt="Moona" style={{ width: 85, height: 75, objectFit: 'contain' }} />
              <img src={banyanImage} alt="Banyan" style={{ width: 85, height: 75, objectFit: 'contain' }} />
            </div>
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          <MDTypography variant="h4" fontWeight="medium" sx={{ color: "#012034", mb: 1, textAlign: "center" }}>
            Reset Password
          </MDTypography>
          <MDTypography variant="button" sx={{ color: "#012034", mb: 3, display: "block", textAlign: "center" }}>
            {step === 1 && "Choose your verification method"}
            {step === 2 && "Enter verification code"}
            {step === 3 && "Create new password"}
          </MDTypography>

          {/* Step 1: Request Reset */}
          {step === 1 && (
            <MDBox component="form" role="form" onSubmit={handleRequestReset}>
              <MDBox mb={3}>
                <MDTypography variant="caption" sx={{ color: "#012034", fontWeight: "medium", mb: 1, display: "block" }}>
                  Verification Method
                </MDTypography>
                <ToggleButtonGroup
                  value={method}
                  exclusive
                  onChange={(e, newMethod) => newMethod && setMethod(newMethod)}
                  fullWidth
                  sx={{
                    '& .MuiToggleButton-root': {
                      color: '#012034',
                      borderColor: '#012034',
                      '&.Mui-selected': {
                        backgroundColor: '#012034',
                        color: '#ffffff',
                        '&:hover': {
                          backgroundColor: '#023456',
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="email">
                    <EmailIcon sx={{ mr: 1 }} />
                    Email
                  </ToggleButton>
                  <ToggleButton value="phone">
                    <PhoneIcon sx={{ mr: 1 }} />
                    Phone
                  </ToggleButton>
                </ToggleButtonGroup>
              </MDBox>

              {method === 'email' && (
                <MDBox mb={2}>
                  <MDInput
                    type="email"
                    label="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                  />
                </MDBox>
              )}

              {method === 'phone' && (
                <MDBox mb={2}>
                  <MDInput
                    type="tel"
                    label="Phone Number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    fullWidth
                    required
                    autoFocus
                  />
                </MDBox>
              )}

              <MDBox mt={4} mb={1}>
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <ColorRing
                      visible={true}
                      height="80"
                      width="80"
                      ariaLabel="color-ring-loading"
                      colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
                    />
                  </div>
                ) : (
                  <Button
                    type="submit"
                    variant="gradient"
                    fullWidth
                    sx={{
                      backgroundColor: "#012034",
                      color: "#ffffff",
                      padding: "10px 16px",
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: "#023456",
                      },
                    }}
                  >
                    Send Verification Code
                  </Button>
                )}
              </MDBox>
            </MDBox>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <MDBox component="form" role="form" onSubmit={handleVerifyCode}>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  fullWidth
                  required
                  autoFocus
                  inputProps={{ maxLength: 6 }}
                />
                <MDTypography variant="caption" sx={{ color: "#012034", mt: 1, display: "block" }}>
                  Enter the 6-digit code sent to your {method === 'email' ? 'email' : 'phone'}
                </MDTypography>
              </MDBox>

              {/* Resend OTP Section */}
              <MDBox mt={2} mb={2} textAlign="center">
                {canResend ? (
                  <Button
                    onClick={handleResendCode}
                    disabled={loading}
                    sx={{
                      color: "#012034",
                      fontSize: "0.75rem",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(1, 32, 52, 0.04)",
                      },
                    }}
                  >
                    Resend Code
                  </Button>
                ) : (
                  <MDTypography variant="caption" sx={{ color: "#666", fontSize: "0.75rem" }}>
                    Resend code in {resendTimer}s
                  </MDTypography>
                )}
              </MDBox>

              <MDBox mt={4} mb={1}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      onClick={resetFlow}
                      variant="outlined"
                      fullWidth
                      sx={{
                        color: "#012034",
                        borderColor: "#012034",
                        padding: "10px 16px",
                        fontSize: "0.875rem",
                        height: "42px",
                        "&:hover": {
                          borderColor: "#023456",
                          backgroundColor: "rgba(1, 32, 52, 0.04)",
                        },
                      }}
                    >
                      Back
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    {loading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '42px' }}>
                        <ColorRing
                          visible={true}
                          height="42"
                          width="42"
                          ariaLabel="color-ring-loading"
                          colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
                        />
                      </div>
                    ) : (
                      <Button
                        type="submit"
                        variant="gradient"
                        fullWidth
                        sx={{
                          backgroundColor: "#012034",
                          color: "#ffffff",
                          padding: "10px 16px",
                          fontSize: "0.875rem",
                          height: "42px",
                          "&:hover": {
                            backgroundColor: "#023456",
                          },
                        }}
                      >
                        Verify Code
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </MDBox>
            </MDBox>
          )}

          {/* Step 3: Reset Password */}
          {step === 3 && (
            <MDBox component="form" role="form" onSubmit={handleResetPassword}>
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth
                  required
                  autoFocus
                  inputProps={{ minLength: 6 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              <MDBox mb={2}>
                <MDInput
                  type={showConfirmPassword ? "text" : "password"}
                  label="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  required
                  inputProps={{ minLength: 6 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>

              <MDBox mt={4} mb={1}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button
                      onClick={() => setStep(2)}
                      variant="outlined"
                      fullWidth
                      sx={{
                        color: "#012034",
                        borderColor: "#012034",
                        padding: "10px 16px",
                        fontSize: "0.875rem",
                        height: "42px",
                        "&:hover": {
                          borderColor: "#023456",
                          backgroundColor: "rgba(1, 32, 52, 0.04)",
                        },
                      }}
                    >
                      Back
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    {loading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '42px' }}>
                        <ColorRing
                          visible={true}
                          height="42"
                          width="42"
                          ariaLabel="color-ring-loading"
                          colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
                        />
                      </div>
                    ) : (
                      <Button
                        type="submit"
                        variant="gradient"
                        fullWidth
                        sx={{
                          backgroundColor: "#012034",
                          color: "#ffffff",
                          padding: "10px 16px",
                          fontSize: "0.875rem",
                          height: "42px",
                          "&:hover": {
                            backgroundColor: "#023456",
                          },
                        }}
                      >
                        Reset Password
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </MDBox>
            </MDBox>
          )}

          {/* Back to Login Link */}
          <MDBox mt={3} mb={1} textAlign="center">
            <MDTypography variant="button" sx={{ color: "#012034" }}>
              Remember your password?{" "}
              <MDTypography
                component={Link}
                to="/authentication/sign-in"
                variant="button"
                sx={{ color: "#012034" }}
                fontWeight="medium"
                textGradient
              >
                Sign in
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>

      {/* Toast Messages at Bottom */}
      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setMessage('')}
          severity="success"
          sx={{
            width: 'auto',
            minWidth: '250px',
            maxWidth: '400px',
            backgroundColor: '#d4edda',
            color: '#155724',
            fontSize: '0.813rem',
            padding: '6px 12px',
            '& .MuiAlert-icon': {
              fontSize: '1.2rem',
              color: '#28a745'
            },
            '& .MuiAlert-message': {
              padding: '4px 0'
            }
          }}
        >
          {message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setError('')}
          severity="error"
          sx={{
            width: 'auto',
            minWidth: '250px',
            maxWidth: '400px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            fontSize: '0.813rem',
            padding: '6px 12px',
            '& .MuiAlert-icon': {
              fontSize: '1.2rem',
              color: '#dc3545'
            },
            '& .MuiAlert-message': {
              padding: '4px 0'
            }
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </BasicLayout>
  );
};

export default ForgotPassword;